import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Menu, ChevronDown, Search } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useSidebar } from "../../contexts/SidebarContext";
import { useVendorProfile } from "../../services/vendorProfileService";
import LogoutModal from "./LogoutModal";
import Button from "./Button";
import { useVendorVerification } from "../../hooks/useVendorVerification";
import NotificationBell from "./NotificationBell";
import VendorBroadcastModal from "../vendor/VendorBroadcastModal";
import GlobalSearchModal from "./GlobalSearchModal";
import PageRefreshButton from "./PageRefreshButton";

const VendorHeader = () => {
  const { user, logout } = useAuth();
  const { toggleVendorMobileSidebar } = useSidebar();
  const navigate = useNavigate();
  const { isVendorVerified, isVerificationPending } = useVendorVerification();
  const { data: profileResp } = useVendorProfile();
  const profile = profileResp?.data || user?.vendor_profile || null;
  const businessName = profile?.business_name || null;
  const businessLogoUrl =
    profile?.logo_url || profile?.brand_settings?.logo_url || null;

  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [businessLogoFailed, setBusinessLogoFailed] = useState(false);
  const [broadcastModal, setBroadcastModal] = useState({ isOpen: false, notification: null });
  const dropdownRef = useRef(null);

  useEffect(() => {
    setBusinessLogoFailed(false);
  }, [businessLogoUrl]);

  useEffect(() => {
    const handleShowBroadcast = (e) => {
      if (e.detail?.notification) {
        setBroadcastModal({ isOpen: true, notification: e.detail.notification });
      }
    };
    window.addEventListener("vendor:showBroadcastModal", handleShowBroadcast);
    return () => window.removeEventListener("vendor:showBroadcastModal", handleShowBroadcast);
  }, []);

  const handleLogout = async () => {
    await logout();
    setShowLogoutModal(false);
    setShowUserDropdown(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setShowSearchModal(true);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const initial = (businessName || user?.name || "V").charAt(0).toUpperCase();

  return (
    <header className="product-topbar sticky top-0 z-30">
      <div className="flex h-12 items-center justify-between gap-2 px-3 sm:px-4 lg:px-6">
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={toggleVendorMobileSidebar}
            className="-ml-1 rounded-md p-1.5 text-ink-secondary hover:bg-surface-muted hover:text-ink lg:hidden"
            aria-label="Open menu"
          >
            <Menu className="h-4 w-4" />
          </button>

          <Link
            to="/vendor"
            className="group flex max-w-[16rem] min-w-0 items-center gap-2 rounded-md px-1 py-0.5 transition-colors hover:bg-surface-muted lg:max-w-xs"
            title={businessName || "Vendor dashboard"}
          >
            {businessLogoUrl && !businessLogoFailed ? (
              <img
                src={businessLogoUrl}
                alt={businessName || "Business logo"}
                className="h-7 w-7 shrink-0 rounded-md border border-border bg-surface object-cover"
                onError={() => setBusinessLogoFailed(true)}
              />
            ) : (
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-ink">
                <span className="text-xs font-semibold text-white">{initial}</span>
              </span>
            )}
            <div className="hidden min-w-0 flex-col leading-none sm:flex">
              <span className="truncate text-sm font-semibold tracking-tight text-ink">
                {businessName || user?.name || "Vendor"}
              </span>
              <span className="mt-0.5 text-[10px] font-medium uppercase tracking-[0.12em] text-ink-tertiary">
                Vendor
              </span>
            </div>
          </Link>
        </div>

        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          <button
            type="button"
            onClick={() => setShowSearchModal(true)}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-surface px-2 text-ink-secondary transition-colors hover:bg-surface-muted hover:text-ink sm:px-2.5"
            title="Search (⌘K)"
            aria-label="Open search"
          >
            <Search className="h-3.5 w-3.5" />
            <span className="hidden text-xs font-medium sm:inline">Search</span>
            <kbd className="ml-1 hidden rounded border border-border bg-surface-muted px-1 py-0.5 text-[10px] text-ink-tertiary md:inline">
              ⌘K
            </kbd>
          </button>

          <PageRefreshButton />

          {!isVendorVerified && (
            <div className="relative hidden sm:block">
              <Button
                onClick={() => {
                  if (!isVerificationPending) navigate("/vendor/verification");
                }}
                variant={isVerificationPending ? "secondary" : "primary"}
                size="sm"
                className={`relative z-10 !rounded-product-md ${ isVerificationPending ? "cursor-default" : "" }`}
                disabled={isVerificationPending}
              >
                {isVerificationPending ? "Pending approval" : "Verify identity"}
              </Button>
            </div>
          )}

          <NotificationBell
            onBroadcastClick={(notification) => {
              setBroadcastModal({ isOpen: true, notification });
            }}
          />

          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setShowUserDropdown(!showUserDropdown)}
              className="flex items-center gap-1.5 rounded-full py-0.5 pl-0.5 pr-1.5 transition-colors hover:bg-surface-muted sm:gap-2 sm:pr-2"
              aria-expanded={showUserDropdown}
              aria-haspopup="menu"
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-ink ring-2 ring-surface">
                <span className="text-xs font-semibold text-white">{initial}</span>
              </div>
              <ChevronDown className="hidden h-3.5 w-3.5 text-ink-tertiary sm:block" />
            </button>

            {showUserDropdown && (
              <div
                className="absolute right-0 z-[10001] mt-2 w-56 overflow-hidden rounded-product-md border border-border bg-surface py-1.5 shadow-sm"
                role="menu"
              >
                <div className="border-b border-border px-4 py-3">
                  {businessName ? (
                    <p className="truncate text-sm font-semibold text-ink">
                      {businessName}
                    </p>
                  ) : null}
                  <p className="truncate text-sm font-medium text-ink">
                    {user?.name || "Vendor"}
                  </p>
                  <p className="mt-0.5 truncate text-xs text-ink-secondary">
                    {user?.email || ""}
                  </p>
                </div>
                <Link
                  to="/vendor/account"
                  onClick={() => setShowUserDropdown(false)}
                  className="flex items-center px-4 py-2 text-sm text-ink hover:bg-surface-muted"
                  role="menuitem"
                >
                  Account settings
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    setShowUserDropdown(false);
                    setShowLogoutModal(true);
                  }}
                  className="flex w-full items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  role="menuitem"
                >
                  <LogOut className="w-4 h-4 mr-3" />
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
      />

      <VendorBroadcastModal
        isOpen={broadcastModal.isOpen}
        onClose={() => setBroadcastModal({ isOpen: false, notification: null })}
        notification={broadcastModal.notification}
      />

      <GlobalSearchModal
        isOpen={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        variant="vendor"
      />
    </header>
  );
};

export default VendorHeader;
